import type { ReactNode } from "react";
import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { X, GripVertical, RotateCcw, RotateCw, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { ExteriorImage, DamageImage } from "@/types/vehicleTest";
import type { FileObject } from "@/types/shared/file";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";
import { isVideoMedia } from "@/lib/isVideoMedia";

type ImageItem = ExteriorImage | DamageImage | FileObject;

interface SortableImageGridProps {
  images: ImageItem[];
  section: string;
  onImageClick: (image: any, alt: string) => void;
  getThumbnailUrl?: (image: any) => string;
  onRemoveImage: (index: number, tireType?: "primary" | "secondary") => void;
  onReorder: (newOrder: ImageItem[]) => void;
  tireType?: "primary" | "secondary";
  /** Optional slot for upload button so it sits in the same grid */
  children?: React.ReactNode;
  onRotate?: (image: any, direction: "left" | "right") => void;
  /** When set, the image with this id shows a loader and is not clickable/draggable */
  rotatingImageId?: string | null;
  /** Session-only rotation in degrees (90, 180, 270) for thumbnail display */
  getImageRotation?: (imageId: string) => number;
  /** Rendered below each image cell (e.g. damage comments when reordering) */
  rowFooter?: (index: number) => ReactNode;
  /** Exterior: index 0 is thumbnail (not separate upload). */
  useIndexZeroAsThumbnail?: boolean;
}

function SortableImageItem({
  img,
  index,
  section,
  onImageClick,
  getThumbnailUrl,
  onRemoveImage,
  onRotate,
  tireType,
  isExteriorThumbnail,
  rotatingImageId,
  getImageRotation,
  rowFooter,
  useIndexZeroAsThumbnail,
}: {
  img: ImageItem;
  index: number;
  section: string;
  onImageClick: (image: any, alt: string) => void;
  getThumbnailUrl?: (image: any) => string;
  onRemoveImage: (index: number, tireType?: "primary" | "secondary") => void;
  onRotate?: (image: any, direction: "left" | "right") => void;
  tireType?: "primary" | "secondary";
  isExteriorThumbnail?: boolean;
  rotatingImageId?: string | null;
  getImageRotation?: (imageId: string) => number;
  rowFooter?: ReactNode;
  useIndexZeroAsThumbnail?: boolean;
}) {
  const itemId = img.id ?? (img as any).imageId;
  const isRotating = !!rotatingImageId && itemId === rotatingImageId;
  const rotationDeg = getImageRotation?.(itemId) ?? 0;
  const { language } = useLanguage();
  const L = listingPostLabels(language);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: img.id || `img-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative min-w-0 w-full max-w-full ${isDragging ? "z-50 opacity-90" : ""} ${isRotating ? "pointer-events-none" : ""}`}
    >
      <div className="relative group flex items-stretch gap-0.5">
        <button
          type="button"
          disabled={isRotating}
          className="flex items-center justify-center shrink-0 w-6 h-24 rounded-l border border-r-0 border-input bg-muted/50 cursor-grab active:cursor-grabbing touch-none disabled:opacity-60 disabled:cursor-not-allowed"
          {...attributes}
          {...listeners}
          aria-label="Ändra ordning"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
        <div className="relative flex-1 min-w-0 overflow-hidden">
          <div
            className="w-full h-full"
            style={
              rotationDeg ? { transform: `rotate(${rotationDeg}deg)` } : undefined
            }
          >
            {(() => {
              const thumbSrc = getThumbnailUrl
                ? getThumbnailUrl(img)
                : img.url;
              const videoSrc =
                thumbSrc && !thumbSrc.includes("#")
                  ? `${thumbSrc}#t=0.001`
                  : thumbSrc;
              return isVideoMedia(img.type, thumbSrc) ? (
              <video
                src={videoSrc}
                muted
                playsInline
                preload="metadata"
                className={`w-full h-24 object-cover rounded-r border ${!isRotating ? "cursor-pointer" : ""} ${
                  isExteriorThumbnail ? "border-blue-500 border-2" : ""
                }`}
                onClick={() =>
                  !isRotating && onImageClick(img, `${section} ${index + 1}`)
                }
              />
            ) : (
              <img
                src={thumbSrc}
                alt={`${section} ${index + 1}`}
                className={`w-full h-24 object-cover rounded-r border ${!isRotating ? "cursor-pointer" : ""} ${
                  isExteriorThumbnail ? "border-blue-500 border-2" : ""
                }`}
                onClick={() =>
                  !isRotating && onImageClick(img, `${section} ${index + 1}`)
                }
                loading="lazy"
              />
            );
            })()}
          </div>
          {isRotating && (
            <div className="absolute inset-0 flex items-center justify-center rounded-r border bg-black/40 z-20">
              <Loader2 className="h-8 w-8 text-white animate-spin" />
            </div>
          )}
          <Badge
            variant="secondary"
            className="absolute top-1 left-1 h-6 min-w-6 flex items-center justify-center rounded-full bg-black/70 text-white text-xs font-semibold"
          >
            {index + 1}
          </Badge>
          {onRotate &&
            !isVideoMedia(
              img.type,
              getThumbnailUrl ? getThumbnailUrl(img) : img.url,
            ) && (
            <div className="absolute bottom-1 left-1 flex gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRotate(img, "left");
                }}
                className="p-1 rounded bg-black/60 hover:bg-black/80 text-white"
                aria-label="Rotera vänster"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRotate(img, "right");
                }}
                className="p-1 rounded bg-black/60 hover:bg-black/80 text-white"
                aria-label="Rotera höger"
              >
                <RotateCw className="h-4 w-4" />
              </button>
            </div>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveImage(index, tireType);
            }}
            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
          >
            <X className="h-5 w-5" />
          </button>
          {section === "exterior" && isExteriorThumbnail && (
            <Badge
              variant="secondary"
              className="absolute bottom-1 left-1 bg-blue-500 text-white text-xs"
            >
              {L.images?.mainImage ?? "Huvudbild"}
            </Badge>
          )}
        </div>
      </div>
      {rowFooter}
    </div>
  );
}

export const SortableImageGrid: React.FC<SortableImageGridProps> = ({
  images,
  section,
  onImageClick,
  getThumbnailUrl,
  onRemoveImage,
  onReorder,
  onRotate,
  rotatingImageId,
  getImageRotation,
  tireType,
  children,
  rowFooter,
  useIndexZeroAsThumbnail = false,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = images.findIndex(
      (img, i) => (img.id || `img-${i}`) === active.id,
    );
    const newIndex = images.findIndex(
      (img, i) => (img.id || `img-${i}`) === over.id,
    );
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(images, oldIndex, newIndex);
    onReorder(newOrder);
  };

  const sortableIds = images.map((img, i) => img.id || `img-${i}`);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-min">
          {images.map((img, index) => (
            <SortableImageItem
              key={img.id || index}
              img={img}
              index={index}
              section={section}
              onImageClick={onImageClick}
              getThumbnailUrl={getThumbnailUrl}
              onRemoveImage={onRemoveImage}
              onRotate={onRotate}
              tireType={tireType}
              rotatingImageId={rotatingImageId}
              getImageRotation={getImageRotation}
              isExteriorThumbnail={
                section === "exterior" &&
                (useIndexZeroAsThumbnail
                  ? index === 0
                  : (img as ExteriorImage).isThumbnail)
              }
              rowFooter={rowFooter?.(index)}
            />
          ))}
          {children}
        </div>
      </SortableContext>
    </DndContext>
  );
};
